import Game from "../Game";

interface AudioInstance {
  sound: Phaser.Sound.BaseSound | null;
  inUse: boolean;
  key: string;
  volume: number;
  loop: boolean;
  cleanup?: () => void;
}

/**
 * Audio Pool for efficient sound instance management
 * Prevents constant creation/destruction of sound objects
 */
export default class AudioPool {
  private scene: Phaser.Scene;
  private audioInstances: Map<string, AudioInstance[]> = new Map();
  private maxInstancesPerSound: number = 5; // Limit concurrent instances per sound
  private totalMaxInstances: number = 20; // Total audio instances limit

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Play a sound using the pool
   * @param key - Sound key
   * @param config - Audio configuration
   * @returns Promise that resolves when sound completes (for cleanup)
   */
  playSound(
    key: string,
    config: {
      volume?: number;
      loop?: boolean;
      onComplete?: () => void;
    } = {}
  ): Promise<void> {
    return new Promise((resolve) => {
      // Check if sounds are enabled
      if ((Game as any).Core?.PlayerOptions?.SoundsEnabled === false) {
        resolve();
        return;
      }

      const { volume = 0.5, loop = false, onComplete } = config;

      // Get or create audio instances array for this sound key
      if (!this.audioInstances.has(key)) {
        this.audioInstances.set(key, []);
      }

      const instances = this.audioInstances.get(key)!;

      // Find available instance or create new one
      let audioInstance = instances.find((instance) => !instance.inUse);

      if (!audioInstance) {
        // Check if we're at the limit for this sound
        if (instances.length >= this.maxInstancesPerSound) {
          // Stop oldest instance and reuse it
          const oldestInstance = instances[0];
          this.stopAudioInstance(oldestInstance);
          audioInstance = oldestInstance;
        } else {
          // Check total instances limit
          const totalInstances = Array.from(
            this.audioInstances.values()
          ).reduce((total, arr) => total + arr.length, 0);

          if (totalInstances >= this.totalMaxInstances) {
            // Find and stop least recently used instance
            const oldestInstance = this.findOldestInstance();
            if (oldestInstance) {
              this.stopAudioInstance(oldestInstance);
              audioInstance = oldestInstance;
            } else {
              resolve();
              return;
            }
          } else {
            // Create new instance
            audioInstance = this.createAudioInstance(key);
            instances.push(audioInstance);
          }
        }
      }

      // Configure and play the audio instance
      this.configureAndPlayAudio(audioInstance, {
        key,
        volume,
        loop,
        onComplete: () => {
          if (onComplete) onComplete();
          resolve();
        },
      });
    });
  }

  private createAudioInstance(key: string): AudioInstance {
    return {
      sound: null,
      inUse: false,
      key,
      volume: 0.5,
      loop: false,
    };
  }

  private configureAndPlayAudio(
    instance: AudioInstance,
    config: {
      key: string;
      volume: number;
      loop: boolean;
      onComplete: () => void;
    }
  ): void {
    // Create new sound if needed or config changed significantly
    if (!instance.sound || instance.key !== config.key) {
      // Destroy old sound if it exists
      if (instance.sound && instance.sound.destroy) {
        instance.sound.destroy();
      }

      instance.sound = this.scene.sound.add(config.key, {
        volume: config.volume,
        loop: config.loop,
      });
    } else {
      // For existing sounds, recreate if volume/loop changed significantly
      if (
        Math.abs(instance.volume - config.volume) > 0.1 ||
        instance.loop !== config.loop
      ) {
        if (instance.sound.destroy) {
          instance.sound.destroy();
        }
        instance.sound = this.scene.sound.add(config.key, {
          volume: config.volume,
          loop: config.loop,
        });
      }
    }

    instance.inUse = true;
    instance.key = config.key;
    instance.volume = config.volume;
    instance.loop = config.loop;

    // Set up completion handler
    const cleanup = () => {
      instance.inUse = false;
      config.onComplete();
    };

    instance.cleanup = cleanup;

    // Play the sound
    if (instance.sound && instance.sound.play) {
      instance.sound.play();

      // Set up auto-cleanup for non-looping sounds
      if (!config.loop) {
        instance.sound.once("complete", cleanup);
      }
    } else {
      // Fallback if sound creation failed
      cleanup();
    }
  }

  private stopAudioInstance(instance: AudioInstance): void {
    if (instance.sound && instance.sound.stop) {
      instance.sound.stop();
    }
    if (instance.cleanup) {
      instance.cleanup();
    }
    instance.inUse = false;
  }

  private findOldestInstance(): AudioInstance | null {
    for (const instances of this.audioInstances.values()) {
      for (const instance of instances) {
        if (instance.inUse) {
          return instance;
        }
      }
    }
    return null;
  }

  /**
   * Stop all sounds of a specific key
   */
  stopAllSounds(key?: string): void {
    if (key) {
      const instances = this.audioInstances.get(key);
      if (instances) {
        instances.forEach((instance) => this.stopAudioInstance(instance));
      }
    } else {
      // Stop all sounds
      for (const instances of this.audioInstances.values()) {
        instances.forEach((instance) => this.stopAudioInstance(instance));
      }
    }
  }

  /**
   * Cleanup and dispose of all audio instances
   */
  destroy(): void {
    for (const instances of this.audioInstances.values()) {
      instances.forEach((instance) => {
        this.stopAudioInstance(instance);
        if (instance.sound && instance.sound.destroy) {
          instance.sound.destroy();
        }
      });
    }
    this.audioInstances.clear();
  }

  /**
   * Get pool statistics for monitoring
   */
  getStats(): {
    totalInstances: number;
    activeInstances: number;
    soundKeys: string[];
  } {
    let totalInstances = 0;
    let activeInstances = 0;
    const soundKeys: string[] = [];

    for (const [key, instances] of this.audioInstances) {
      soundKeys.push(key);
      totalInstances += instances.length;
      activeInstances += instances.filter((instance) => instance.inUse).length;
    }

    return {
      totalInstances,
      activeInstances,
      soundKeys,
    };
  }
}
